import {
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { toSignal } from '@angular/core/rxjs-interop';
import { invitesSidebarActions } from './invites-sidebar.actions';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';
import { DropdownComponent } from '@app/framework/inputs/dropdown/dropdown.component';
import { ContactsStore } from '@app/store/contacts/contacts.store';
import {
  selectAreSimpleProjectsLoading,
  selectNonDeletedSimpleProjects,
} from '@app/store/projects/projects.selectors';
import { SimpleButtonComponent } from '@app/framework/buttons/simple-medium-button/simple-button.component';
import { InvitationsStore } from '@app/store/invitations/invitations.store';
import { CurrentUserService } from '@app/services/current-user.service';
import {
  InviteForOption,
  InviteModel,
  PrimeInviteGCPayload,
  PrimeInviteManagerPayload,
} from '@app/store/invitations/invitations.types';
import { LoadingSpinnerComponent } from '@app/framework/loading-animation/loading-spinner.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';

@Component({
  selector: 'app-invites-sidebar',
  imports: [
    SidebarHeaderComponent,
    DropdownComponent,
    FormsModule,
    SimpleButtonComponent,
    LoadingSpinnerComponent,
    TooltipDirective,
  ],
  templateUrl: './invites-sidebar.component.html',
  styleUrl: './invites-sidebar.component.scss',
})
export class InvitesSidebarComponent implements OnInit {
  readonly contactsStore = inject(ContactsStore);
  readonly invitationsStore = inject(InvitationsStore);
  readonly userService = inject(CurrentUserService);
  readonly store = inject(Store);

  markAsSubmitted = input<boolean>(false);
  projectId = input<number | null>(null);
  contactId = input<number | null>(null);
  inviteFor = input<InviteForOption | null>(null);

  model = linkedSignal<InviteModel>(() => ({
    project_id: this.projectId(),
    contact_id: this.contactId(),
    invite_for: this.inviteFor(),
  }));

  contacts = this.contactsStore.contacts;
  inviteForOptions: WritableSignal<{ id: InviteForOption }[]> = signal([
    { id: 'prime_contract' },
    { id: 'commitment' },
    { id: 'no-commitment' },
  ]);

  projects = toSignal(this.store.select(selectNonDeletedSimpleProjects));
  areSimpleProjectsLoading = toSignal(this.store.select(selectAreSimpleProjectsLoading));
  isLoading = computed(
    () =>
      this.areSimpleProjectsLoading() ||
      this.invitationsStore.isLoading() ||
      this.contactsStore.isLoading(),
  );

  ngOnInit() {
    this.store.dispatch(invitesSidebarActions.loadInvitesSidebar());
    this.contactsStore.loadContactGroups();
  }

  save() {
    if (this.model().invite_for === 'prime_contract') {
      this.invitationsStore.sendPrimeInvite(this.getPrimeInvitePayload(), this.markAsSubmitted());
    } else {
      this.invitationsStore.sendSubcontractInvite(this.model());
    }
  }

  private getPrimeInvitePayload(): PrimeInviteGCPayload | PrimeInviteManagerPayload {
    if (this.userService.isManager) {
      return {
        manager_project_id: this.model().project_id,
        sp_contact_id: this.model().contact_id,
      };
    } else if (this.userService.isPremiumSP) {
      return {
        prime_project_id: this.model().project_id,
        manager_contact_id: this.model().contact_id,
      };
    } else {
      throw new Error('User role currently not supported for invitations.');
    }
  }

  protected updateModel(field: keyof InviteModel, value: unknown) {
    this.model.update((current) => ({
      ...current,
      [field]: value,
    }));
  }
}
